import { useEffect } from "react";

const Terms = () => {
  useEffect(() => {
    document.title = "Terms & Conditions | IA4Solutions";

    const description =
      "Read the IA4Solutions Terms and Conditions covering website use, training programs, registrations, intellectual property, payments, placement support, and other website terms.";

    let metaDescription = document.querySelector(
      'meta[name="description"]'
    );

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute("content", description);

    return () => {
      document.title = "IA4Solutions";
    };
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-gray-950">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-1/4 top-0 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl"
        />

        <div className="container relative mx-auto px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-purple-700 dark:border-purple-900 dark:bg-purple-950/50 dark:text-purple-300">
              Legal Information
            </span>

            <h1 className="mt-5 text-3xl font-bold leading-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
              Terms &amp; Conditions
            </h1>

            <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-gray-600 dark:text-gray-300 sm:text-base">
              These Terms &amp; Conditions explain the rules that apply when
              you access or use the IA4Solutions website and related online
              services.
            </p>

            <p className="mt-4 text-xs font-medium text-gray-500 dark:text-gray-400">
              Last updated: September 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 py-12 sm:px-6 md:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl space-y-10">

          {/* Acceptance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              1. Acceptance of These Terms
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              By accessing or using the IA4Solutions website, you agree to
              comply with these Terms &amp; Conditions and applicable laws.
              If you do not agree with these terms, please do not use the
              website.
            </p>
          </section>

          {/* Website Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              2. Website Use
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              You may use this website for lawful purposes, including
              learning about our courses, training programs, internships,
              services, career support, and contacting IA4Solutions.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              You must not use the website in a way that could damage the
              website, interfere with its operation, compromise security,
              misuse information, or violate applicable law.
            </p>
          </section>

          {/* Course Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              3. Course and Training Information
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Course descriptions, technologies, schedules, training formats,
              fees, availability, duration, and other program information may
              be updated from time to time.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              The information displayed on the website is intended to provide
              general information about our programs. Specific enrollment
              details communicated during registration may apply to an
              individual learner or program.
            </p>
          </section>

          {/* Registration */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              4. Registration and Enrolment
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Where registration is required, users are responsible for
              providing accurate and complete information and keeping submitted
              information up to date.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              IA4Solutions may contact you regarding your registration,
              training requirements, course information, or related services.
            </p>
          </section>

          {/* Payments */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              5. Fees, Payments, Cancellations and Refunds
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Where a course or service requires payment, applicable fees and
              payment terms will be communicated during the relevant
              registration or enrollment process.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              Any cancellation, refund, rescheduling, or payment conditions
              specifically communicated for a course or service will apply to
              that transaction, subject to applicable law.
            </p>
          </section>

          {/* Placement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              6. Career and Placement Support
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              IA4Solutions may provide career guidance, interview preparation,
              placement assistance, industry interaction, or related support
              depending on the program or service.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              Such support does not by itself constitute a guarantee of
              employment, salary, interview selection, internship placement,
              or any specific career outcome.
            </p>
          </section>

          {/* User Content */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              7. User Information and Submissions
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              When submitting information through forms, enquiries, reviews,
              registrations, or other features, you agree to provide
              information that is accurate and lawful.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              You must not submit content that is unlawful, misleading,
              abusive, defamatory, infringing, or harmful to other users or
              the website.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              8. Intellectual Property
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Unless otherwise stated, website content including text,
              graphics, logos, branding, visual elements, and original
              materials is owned by or used by IA4Solutions with appropriate
              rights.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              You may not reproduce, redistribute, modify, publish, sell, or
              commercially exploit website content without appropriate
              permission, except where permitted by law.
            </p>
          </section>

          {/* Third Parties */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              9. Third-Party Services and Links
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              The website may contain links to third-party websites, social
              platforms, video services, payment providers, or other external
              services.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              IA4Solutions does not control every third-party service and is
              not responsible for the content, availability, policies, or
              practices of external websites.
            </p>
          </section>

          {/* Availability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              10. Website Availability
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              We aim to keep the website useful and available, but continuous
              or uninterrupted availability cannot be guaranteed. Maintenance,
              updates, technical issues, third-party failures, or circumstances
              outside our reasonable control may affect availability.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              11. Disclaimer
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Website information is provided for general informational and
              educational purposes. While we aim to keep information accurate
              and useful, we do not guarantee that every piece of information
              will always be complete, current, or error-free.
            </p>
          </section>

          {/* Limitation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              12. Limitation of Liability
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              To the extent permitted by applicable law, IA4Solutions will not
              be responsible for losses arising from website interruption,
              reliance on general website information, third-party services,
              unauthorized use, or circumstances beyond our reasonable
              control.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              13. Changes to These Terms
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              IA4Solutions may update these Terms &amp; Conditions from time to
              time to reflect changes in our services, website functionality,
              business practices, or applicable requirements.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              14. Applicable Law
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              These Terms are intended to be interpreted subject to applicable
              laws and regulations. Nothing in these Terms is intended to
              restrict rights or protections that cannot lawfully be excluded.
            </p>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900/60">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              15. Contact Us
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              For questions about these Terms &amp; Conditions, contact
              IA4Solutions:
            </p>

            <div className="mt-4 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:hr@ia4solutions.com"
                  className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
                >
                  hr@ia4solutions.com
                </a>
              </p>

              <p>
                <strong>Phone:</strong> 8332029863 | 8008638374
              </p>

              <p>
                <strong>Address:</strong> #23-7-168, Santhi Nagar, M.R.Palli
                Circle, Tirupati – 517502
              </p>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default Terms;